/**
 * complexityVocabulary — the one Hebrew vocabulary for a run's own complexity, the third of the
 * three classification fields an analysis carries beside `risk_level` and `task_type`.
 *
 * The scale is the closed three-arm `low | medium | high` declared twice in the same producer:
 * `_VALID_COMPLEXITIES` (`backend/app/services/direct_llm_analyst.py`:112) is the set the parser
 * validates against, and `:215` writes the same three arms into the model's own output-format
 * example. The value reaches the frontend as `AgentRun.analysis["complexity"]`, where
 * `manager_agent.py`:2947 puts it (`dataclasses.asdict(output)`) and `_record_usage` (`:436`) also
 * copies it onto the `AgentRun.complexity` column.
 *
 * Three writers mint it and all three are inside the scale: the provider path validates the model's
 * answer against `_VALID_COMPLEXITIES` and falls back to `"medium"` (`:947-949`), and the two
 * deterministic fallbacks are literals — `helpdesk_analyst.py`:399 and `direct_llm_analyst.py`:1073,
 * both `"medium"`. **On the pilot default (no `ANALYSIS_PROVIDER`) every run therefore carries
 * `"medium"`**, which is why the tooltip defect below is reachable on the default build rather than
 * only on a provider-configured one.
 *
 * The frontend did not treat the scale as one field. Four render sites in three files worded it
 * three different ways, and not one of them could say "this build does not know this value":
 *
 *   - `AgentRunsDashboardPage`'s stats strip printed the producer's own token inside a Hebrew
 *     tooltip — `מורכבות: medium` — because it interpolated the raw key rather than wording it;
 *   - the same page's row cell and the strip both used an inline ternary chain whose *last* arm
 *     was `"מורכב"`, so any value that was neither `"low"` nor `"medium"` was worded as the
 *     **highest** grade — a claim, not an unlabelled gap;
 *   - `AgentRunPanel` and `AgentRunDetailPage` each kept their own `COMPLEXITY_HE` map, with
 *     *different words for the same arms*, and both fell through to the raw token;
 *   - and every colour ternary on the field ended in `else → the lowest grade`, so an unrecognised
 *     value was drawn as the *simplest* thing on screen while the dashboard worded the same value
 *     as the most complex and the panel printed it raw. Colour and word disagreed.
 *
 * Two rules this module exists to hold, the same two `riskGradeVocabulary` and
 * `runStatusVocabulary` hold:
 *
 *   1. One scale, re-derived from the producer. `complexityVocabulary.test.js` parses
 *      `_VALID_COMPLEXITIES` out of the Python source and asserts this map's key set EQUALS it, and
 *      re-derives the same three arms a second, independent way from the output-format example, so
 *      a fourth arm, a renamed arm or a dropped one reddens the suite instead of reaching an
 *      operator as a bare English word.
 *   2. An unrecognised value is NAMED, never echoed — the reason `complexityLabel` will not put a
 *      token on screen. A value outside the scale means this bundle is behind the producer, or the
 *      row predates the validation; either way the operator is told that, not shown `medium`.
 *
 * Wording. The three words are the ones three of the four sites already used (`פשוט` / `בינוני` /
 * `מורכב`), so only `AgentRunDetailPage`'s frames change. They are bare adjectives of the *ticket*
 * rather than of the noun `מורכבות`, which matters on this codebase for one reason: the feminine
 * level wording the fourth site used (`נמוכה` / `בינונית` / `גבוהה`) is word for word the ticket
 * **priority** scale — `TICKET_PRIORITY_LABEL` in `GuardianCenterPage`, `PRIORITY_LABELS` in
 * `TicketDetail`, `PRIORITY_HE` in four more files — and the dashboard renders both fields on the
 * same row. This field is complexity, so it must not read as priority.
 *
 * `complexityTone` exists because this scale, unlike the risk grade, is used to pick a colour at
 * five sites that all ended in `else → green`. A tone is not a class: each call site still owns its
 * own classes (as `riskGradeVocabulary`'s header argues), but it must now decide what to draw for a
 * value the scale does not hold instead of inheriting the lowest grade by falling through.
 *
 * WHAT THIS FILE CANNOT SEE, stated so the coverage claim cannot go short. It words `complexity`
 * only. `risk_level` (both the green/amber/red string scale and the 0–3 integer grade) and
 * `task_type` are different fields with their own producers and their own modules — the task-type
 * map is duplicated on the two files this iteration touches and is deliberately left alone. The
 * four Hebrew words this module holds are not shared with any other map in the tree, so a sweep for
 * them cannot be confused by the priority and severity scales that reuse `low`/`medium`/`high` as
 * keys.
 *
 * `complexity` is also the name of a *different* field in the same backend: `ProviderUsage` carries
 * one beside `task_type="diagnosis"` (`openclaw_adapter.py`:795, a usage/cost record of the call the
 * LLM was asked to make), and it is not this scale and not on any frontend surface. That is why the
 * guard's hand-written-literal scan is scoped to the two modules that mint the *analysis* field
 * rather than run over `backend/`: widening it would couple this map to a field whose arms this
 * module does not own, and a sweep for the token alone cannot tell the two apart.
 */

/** The three arms of the producer's complexity scale, by token. */
export const COMPLEXITY_HE = {
  low: "פשוט",
  medium: "בינוני",
  high: "מורכב",
};

/** Shown for a value the scale does not hold. Never the token itself. */
export const COMPLEXITY_UNKNOWN_HE = "רמת מורכבות לא מזוהה";

/** The arm a value outside the scale is drawn as, so no site has to invent a fall-through. */
export const COMPLEXITY_TONE_UNKNOWN = "unknown";

/**
 * The Hebrew word for a stored complexity. `hasOwnProperty` rather than `COMPLEXITY_HE[value] ||`:
 * a bare lookup answers an inherited key (`"constructor"`, `"toString"`) with a function, which a
 * `||` fallback would treat as a hit and render.
 *
 * `null`/`undefined` and a value on another scale (`"amber"`, a priority) both land on the named
 * unknown rather than on an empty string, so a caller cannot print a grade it does not have.
 */
export function complexityLabel(value) {
  return Object.prototype.hasOwnProperty.call(COMPLEXITY_HE, value)
    ? COMPLEXITY_HE[value]
    : COMPLEXITY_UNKNOWN_HE;
}

/**
 * The arm a value should be *drawn* as: one of `low` / `medium` / `high`, or
 * `COMPLEXITY_TONE_UNKNOWN` for anything else.
 *
 * Deliberately not a class name. The five colour ternaries on this field all used their last arm as
 * a catch-all, which is how an unknown value came to be drawn as the lowest grade on one surface and
 * worded as the highest on another; returning a fourth tone makes that collision a choice each call
 * site has to write down.
 */
export function complexityTone(value) {
  return Object.prototype.hasOwnProperty.call(COMPLEXITY_HE, value)
    ? value
    : COMPLEXITY_TONE_UNKNOWN;
}

/**
 * The scale's arms in canonical order — weakest complexity first — for a surface that has to lay
 * several of them out. Read off this module rather than written again at the call site, because the
 * strip that used to hold its own `["low", "medium", "high"]` literal silently dropped any key the
 * backend's distribution carried outside it, and a dropped key is a count that does not reconcile.
 */
export const COMPLEXITY_ORDER = Object.keys(COMPLEXITY_HE);
